import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AdminCredentials } from '../types';

export const Admin: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, isLoading, login } = useAuth();
  const [credentials, setCredentials] = useState<AdminCredentials>({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoginLoading(true);

    const result = await login(credentials);
    if (!result.success) {
      setError(result.error || t('admin.invalidCredentials'));
    }
    setLoginLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center tc-bg-secondary py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold tc-text-primary">
              {t('admin.title')}
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  {t('admin.username')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="tc-input appearance-none rounded-none relative block w-full rounded-t-md focus:z-10"
                  placeholder={t('admin.username')}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  {t('admin.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="tc-input appearance-none rounded-none relative block w-full rounded-b-md focus:z-10"
                  placeholder={t('admin.password')}
                />
              </div>
            </div>

            {error && (
              <div className="error-bg rounded-md p-4">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loginLoading}
                className="tc-btn tc-btn-primary w-full"
              >
                {loginLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  t('admin.login')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Only redirect to dashboard if we're on the exact /admin path
  // If user is accessing /admin/testing or other admin routes, don't redirect
  if (location.pathname === '/admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // This component should only render on /admin route, not on other admin subroutes
  // If we somehow end up here on other routes, it means there's a routing issue
  console.warn('Admin component rendered on non-/admin path:', location.pathname);
  return null;
};